import { useState } from 'react';
import {
  policyRequestApprove,
  policyRequestDecline,
  policyRuleMode,
  policyVersionRollback,
  type PolicyRequest,
  type PolicyRule,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { ruleSentence } from './RuleBuilder';

/** Approve or decline a building's change request. The server lets only the owner (or deputy) decide. */
export function RequestDecision({ req, ok, onClose, onDone }: { req: PolicyRequest; ok: boolean; onClose: () => void; onDone: () => void }) {
  const { client } = useSession();
  const [reason, setReason] = useState('');
  const where = req.facilityName ?? 'the network';
  return (
    <ConfirmDialog
      title={ok ? `Approve the change for ${where}?` : `Decline the change for ${where}?`}
      confirmLabel={ok ? 'Approve' : 'Decline'}
      danger={!ok}
      body={
        <>
          <p>
            {ok
              ? 'It goes live at once, is written as a new policy version and is sealed to the Truth Ledger. The building is told.'
              : 'Nothing changes at the building. The reason is sent to the person who asked and sealed to the Truth Ledger.'}
          </p>
          <ul>
            {req.items.map((it, i) => (
              <li key={i}>
                <b>{it.guard}</b> {it.change}
              </li>
            ))}
          </ul>
          {!ok && (
            <label className="field">
              <span className="field__label">Reason (required)</span>
              <textarea className="field__input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            </label>
          )}
        </>
      }
      onConfirm={async () => {
        if (ok) await policyRequestApprove(client, { params: { id: req.id }, body: withConfirm({}) });
        else {
          if (!reason.trim()) throw Object.assign(new Error('A reason is required to decline.'), { kind: 'validation', requestId: null });
          await policyRequestDecline(client, { params: { id: req.id }, body: withConfirm({ reason: reason.trim() }) });
        }
        onDone();
      }}
      onClose={onClose}
    />
  );
}

/** Roll back: the server creates a NEW draft carrying the old snapshot; it goes live only when saved. */
export function RollbackDialog({ id, label, onClose, onDone }: { id: string; label: string; onClose: () => void; onDone: (draftId: string) => void }) {
  const { client } = useSession();
  return (
    <ConfirmDialog
      title={`Roll back to ${label}?`}
      confirmLabel="Load as a draft"
      body={<p>That version is loaded as a draft. It does not apply until you review and save it, so a rollback is itself a reviewed, ledgered change.</p>}
      onConfirm={async () => {
        const r = await policyVersionRollback(client, { params: { id }, body: withConfirm({}) });
        onDone(r.version.id);
      }}
      onClose={onClose}
    />
  );
}

/**
 * Watch to enforce is stricter: a request the owner approves (the owner's own change applies
 * at once). Enforce back to watch is Aithora staff only, with a reason.
 */
export function RuleModeDialog({ rule, isOp, onClose, onDone }: { rule: PolicyRule; isOp: boolean; onClose: () => void; onDone: () => void }) {
  const { client } = useSession();
  const [reason, setReason] = useState('');
  const toEnforce = rule.mode === 'watch';
  return (
    <ConfirmDialog
      title={toEnforce ? 'Start enforcing this rule?' : 'Move this rule back to watch only?'}
      confirmLabel={toEnforce ? 'Ask to enforce' : 'Move to watch'}
      body={
        <>
          <p>{ruleSentence(rule)}</p>
          <p>
            {toEnforce
              ? 'Enforcing is stricter, so it goes to the owner for approval. You will see the answer under your requests.'
              : isOp
                ? 'It stops acting and only records what it would have done. This is versioned and sealed to the Truth Ledger.'
                : 'Only Aithora can move a rule back to watch. Ask your Aithora operator.'}
          </p>
          {!toEnforce && isOp && (
            <label className="field">
              <span className="field__label">Reason (required)</span>
              <textarea className="field__input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            </label>
          )}
        </>
      }
      onConfirm={async () => {
        if (!toEnforce && !reason.trim()) throw Object.assign(new Error('A reason is required to move a rule back to watch.'), { kind: 'validation', requestId: null });
        await policyRuleMode(client, { params: { id: rule.id }, body: withConfirm({ mode: toEnforce ? 'enforce' : 'watch', ...(toEnforce ? {} : { reason: reason.trim() }) }) });
        onDone();
      }}
      onClose={onClose}
    />
  );
}
